/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

/**
 * Declares, per custom element tag, which module defines it and when that
 * module should load. A merchant can place any of these components'
 * blocks/sections on any template via the theme editor, so gating by
 * template name can't be proven safe — gating by "is the element actually
 * on this page" (and, further, "is it about to be seen or used") always is.
 *
 * `trigger` is one of:
 * - 'eager': imported the moment its own tag is found anywhere in the
 *   document — the initial `scan(document)` pass, or a later insertion the
 *   MutationObserver below catches — with no IntersectionObserver delay,
 *   for elements that need to be ready before any possible interaction
 *   rather than merely before the element scrolls into view (see
 *   add-to-cart-component/product-form-component below for why).
 * - 'lazy': imported via IntersectionObserver the moment the element is
 *   about to scroll into view. The default for most UI.
 * - 'interaction': imported the moment the visitor starts interacting with
 *   whatever opens/activates the element (resolved through the same
 *   `on:<event>="selector/method"` delegation attributes component.js
 *   itself dispatches through — see `handleInteraction` below). For
 *   elements that are off-canvas/zero-size until triggered — a drawer or
 *   dialog closed until a trigger elsewhere is pressed — where
 *   IntersectionObserver would never fire since there's no visible area to
 *   observe. NOTE: product-variations-drawer and variant-picker-drawer are
 *   NOT 'interaction' despite the name — each wraps its own always-visible
 *   trigger <button> inside the custom element itself (see
 *   product-variations.liquid / variant-drawer-picker.liquid), so the tag
 *   has real on-page size and IntersectionObserver fires normally. Moving
 *   either to 'interaction' would break loading: their "/open" attribute is
 *   on the nested trigger button, not the drawer tag itself, so this
 *   file's resolveInteractionTarget (see below) can't correctly resolve it
 *   the way it does for filters-drawer's `#id`-selector trigger.
 *
 * A plain object here rather than extra fields on the
 * `<script type="importmap">` in scripts.liquid, on purpose: import maps
 * are a browser-native specifier -> URL lookup table, and the spec requires
 * each entry's value to be a URL string — there's no room for "how/when to
 * load" metadata without breaking it. Keeping trigger + specifier together
 * in one place means one file declares both, instead of splitting the
 * decision between this file and a tag list in scripts.liquid.
 *
 * `priority` (default 'high') is a second, orthogonal axis: when several
 * 'eager' or 'lazy' modules become eligible to load in the same tick — the
 * initial `scan(document)` pass, or a big batch of nodes landing from a
 * section swap — they'd otherwise all call `import()` at once and compete
 * for the same network/parse budget. Set 'low' for modules with no UI
 * dependency (view/analytics tracking, not rendering anything) to defer
 * their load to `requestIdleCallback`, so they yield to whatever real UI
 * modules are loading alongside them. Not honoured for 'interaction'
 * modules — the entire point of that trigger is to beat the visitor's
 * click, so deferring to idle time would defeat it. No module currently
 * configured below needs this tier; kept as part of the mechanism (not
 * stripped out) since a future non-UI/analytics module would want it.
 *
 * @typedef {{ specifier: string, trigger: 'eager' | 'lazy' | 'interaction', priority?: 'high' | 'low' }} ModuleConfig
 */

import { INTERACTION_EVENTS } from '@theme/component';

/**
 * Deliberately NOT every module that defines a custom element is listed
 * here — excluded on purpose:
 * - dialog.js: its `toggle` listener manages scroll-lock for ANY
 *   `<details scroll-lock>` element, not scoped to <dialog-component> —
 *   loading it only when <dialog-component> appears would silently drop
 *   scroll-lock for any page using a plain <details scroll-lock> without a
 *   dialog nearby. Stays as its own unconditional <script> tag in
 *   scripts.liquid rather than an entry here.
 * - popover-polyfill.js, email-signup.js: no custom element to key off.
 * - Anything already template/setting-gated in scripts.liquid
 *   (paginated-list*.js, localization.js, sticky-add-to-cart.js,
 *   soft-navigation.js, find-in-store.js, product-card-link.js,
 *   cart-discount.js) or self-contained in its own block template
 *   (comparison-slider.js) — already correctly scoped, no need to touch.
 * - Foundational/dependency-only modules with no customElements.define of
 *   their own (component.js, utilities.js, events.js, focus.js, morph.js,
 *   section-renderer.js, section-hydration.js, scrolling.js,
 *   recently-viewed-products.js) — pulled in transitively by whichever of
 *   the modules below imports them.
 *
 * @type {Record<string, ModuleConfig>}
 */
const MODULES = {
  // Both wired via on:submit on a real <form>-backed component. A dropped
  // submit (module not loaded yet when the browser dispatches the event) is
  // worse than a dropped click: component.js's dispatcher never gets to
  // call preventDefault(), so the browser falls through to a real native
  // form POST to /cart/add — a full page navigation instead of the AJAX
  // add-to-cart flow. pointerdown/click/submit fire back-to-back
  // synchronously in one gesture, faster than any dynamic import() can
  // resolve, so the 'interaction' trigger can't reliably close this gap —
  // these have to be eager. Both tags are their own reliable presence gate:
  // no add-to-cart-component/product-form-component tag anywhere in the DOM
  // means no form to submit, so nothing to protect.
  'add-to-cart-component': { specifier: '@theme/product-form', trigger: 'eager' },
  'product-form-component': { specifier: '@theme/product-form', trigger: 'eager' },

  // Genuinely off-canvas/zero-size until opened — triggered from a separate
  // button elsewhere via on:click="#filters-drawer/showDialog" (an #id
  // selector), not a trigger nested inside the tag itself, so
  // resolveInteractionTarget below resolves it correctly.
  'filters-drawer': { specifier: '@theme/drawer-component', trigger: 'interaction' },

  'overflow-list': { specifier: '@theme/overflow-list', trigger: 'lazy' },
  'quick-add-component': { specifier: '@theme/quick-add', trigger: 'lazy' },
  'quick-add-dialog': { specifier: '@theme/quick-add', trigger: 'lazy' },
  'variant-picker': { specifier: '@theme/variant-picker', trigger: 'lazy' },
  'product-card': { specifier: '@theme/product-card', trigger: 'lazy' },
  'swatches-variant-picker-component': { specifier: '@theme/product-card', trigger: 'lazy' },
  'notify-me-component': { specifier: '@theme/notify-me', trigger: 'lazy' },
  'accordion-custom': { specifier: '@theme/accordion-custom', trigger: 'lazy' },
  'disclosure-custom': { specifier: '@theme/disclosure-custom', trigger: 'lazy' },
  'deferred-media': { specifier: '@theme/media', trigger: 'lazy' },
  'product-model': { specifier: '@theme/media', trigger: 'lazy' },
  'product-price': { specifier: '@theme/product-price', trigger: 'lazy' },
  'card-variation-hover': { specifier: '@theme/card-variation-hover', trigger: 'lazy' },
  // Not 'interaction' — see the doc comment above MODULES for why.
  'product-variations-drawer': { specifier: '@theme/drawer-component', trigger: 'lazy' },
  'variant-picker-drawer': { specifier: '@theme/drawer-component', trigger: 'lazy' },
  'product-variations': { specifier: '@theme/product-variations', trigger: 'lazy' },
  // Eager: it balances the grid's rows before the shopper scrolls to the last one.
  'promo-banner': { specifier: '@theme/promo-banner', trigger: 'eager' },
  // Eager: it listens for variant changes, and placed below the fold it would
  // otherwise miss a size picked before it scrolls into view.
  'product-sku-component': { specifier: '@theme/product-sku', trigger: 'eager' },
  'product-title': { specifier: '@theme/product-title', trigger: 'lazy' },
  'product-inventory': { specifier: '@theme/product-inventory', trigger: 'lazy' },
  'show-more-component': { specifier: '@theme/show-more', trigger: 'lazy' },
  'slideshow-component': { specifier: '@theme/slideshow', trigger: 'lazy' },
  'layered-slideshow-component': { specifier: '@theme/layered-slideshow', trigger: 'lazy' },
  'anchored-popover-component': { specifier: '@theme/anchored-popover', trigger: 'lazy' },
  'floating-panel-component': { specifier: '@theme/floating-panel', trigger: 'lazy' },
  'video-background-component': { specifier: '@theme/video-background', trigger: 'lazy' },
  'quantity-selector-component': { specifier: '@theme/component-quantity-selector', trigger: 'lazy' },
  'media-gallery': { specifier: '@theme/media-gallery', trigger: 'lazy' },
  'rte-formatter': { specifier: '@theme/rte-formatter', trigger: 'lazy' },
  'volume-pricing': { specifier: '@theme/volume-pricing', trigger: 'lazy' },
  'price-per-item': { specifier: '@theme/price-per-item', trigger: 'lazy' },
  'volume-pricing-info': { specifier: '@theme/volume-pricing-info', trigger: 'lazy' },
};

/** Specifiers already requested, so a repeat sighting of the same tag doesn't re-import. */
const requested = new Set();

/** @param {string} specifier */
function loadModule(specifier) {
  if (requested.has(specifier)) return;
  requested.add(specifier);
  import(specifier).catch((error) => {
    console.error(`component-loader: failed to load "${specifier}"`, error);
  });
}

/** Specifiers already given a modulepreload hint, so a repeat sighting doesn't add a duplicate <link>. */
const preloaded = new Set();

/**
 * Starts the network fetch (and, in browsers that parse/compile ahead of
 * execution, that too) for a module without executing it — for 'low'
 * priority modules specifically, whose execution is deliberately delayed
 * (see `schedule` below). Without this, the delay applies to the network
 * fetch too: nothing about the module even starts downloading until the
 * idle callback fires. Preloading immediately means the wait is spent on
 * the network in parallel with everything else already loading, so by the
 * time the idle callback actually calls `import()`, it resolves against an
 * already-warm cache instead of paying for the round trip at that later,
 * still-somewhat-arbitrary moment.
 *
 * @param {string} specifier
 */
function preloadModule(specifier) {
  if (preloaded.has(specifier)) return;
  preloaded.add(specifier);
  try {
    const href = import.meta.resolve(specifier);
    const link = document.createElement('link');
    link.rel = 'modulepreload';
    link.href = href;
    document.head.appendChild(link);
  } catch {
    // Resolution failing here (e.g. a browser without import.meta.resolve)
    // just means no preload hint — the later import() call in loadModule
    // still works and will surface any genuine error on its own.
  }
}

/**
 * Loads a module respecting its declared priority. 'low' priority modules
 * are preloaded immediately (see `preloadModule`) but wait to actually
 * execute for an idle period (with a timeout so they still eventually run
 * under sustained activity, e.g. continuous scrolling) rather than racing
 * whatever UI-critical modules are loading in the same tick.
 *
 * @param {ModuleConfig} config
 */
function schedule(config) {
  if (config.priority !== 'low') {
    loadModule(config.specifier);
    return;
  }

  preloadModule(config.specifier);

  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => loadModule(config.specifier), { timeout: 2000 });
  } else {
    // Safari has no requestIdleCallback — a short timeout still yields to
    // any render/interaction work already queued ahead of it.
    setTimeout(() => loadModule(config.specifier), 1);
  }
}

const eagerTags = [];
const lazyTags = [];
const interactionTags = [];
for (const [tag, config] of Object.entries(MODULES)) {
  if (config.trigger === 'eager') eagerTags.push(tag);
  else if (config.trigger === 'interaction') interactionTags.push(tag);
  else lazyTags.push(tag);
}

const EAGER_SELECTOR = eagerTags.join(',');
const LAZY_SELECTOR = lazyTags.join(',');
const SCAN_SELECTOR = [EAGER_SELECTOR, LAZY_SELECTOR].filter(Boolean).join(',');

// rootMargin starts the fetch slightly before the element is actually on
// screen, so scrolling to it doesn't visibly race the module load.
const intersectionObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer.unobserve(entry.target);
            const config = MODULES[entry.target.localName];
            if (config) schedule(config);
          }
        },
        { rootMargin: '200px' },
      )
    : null;

/** @param {Element} element */
function handleElement(element) {
  if (customElements.get(element.localName)) return; // already defined — nothing to load
  const config = MODULES[element.localName];
  if (!config) return;

  // 'eager' tags load the instant their own presence is confirmed — no
  // IntersectionObserver step, since these need to be ready before any
  // possible interaction, not merely before the element is on screen.
  if (config.trigger === 'eager' || !intersectionObserver) {
    schedule(config);
    return;
  }

  intersectionObserver.observe(element);
}

/** @param {Element | Document | DocumentFragment} root */
function scan(root) {
  if (SCAN_SELECTOR === '') return;
  if (root instanceof Element && root.matches(SCAN_SELECTOR)) handleElement(root);
  for (const element of root.querySelectorAll(SCAN_SELECTOR)) handleElement(element);
}

/**
 * Releases the IntersectionObserver's hold on any 'lazy'-tagged element in a
 * removed subtree. Without this, an element observed while still on the
 * page (e.g. a product-card in a collection grid) but removed before it
 * ever scrolled into view — routine on filter/sort/quick-add re-renders —
 * would stay registered with the observer forever, holding a strong
 * reference that keeps the whole detached subtree from being
 * garbage-collected.
 * @param {Element} root
 */
function unobserveRemoved(root) {
  if (!intersectionObserver || LAZY_SELECTOR === '') return;
  if (root.matches(LAZY_SELECTOR)) intersectionObserver.unobserve(root);
  for (const element of root.querySelectorAll(LAZY_SELECTOR)) intersectionObserver.unobserve(element);
}

scan(document);

/**
 * Callbacks from other, unrelated modules that also need to react to every
 * DOM insertion site-wide (currently just lazy-image-fade.js) — registered
 * here rather than each running its own separate MutationObserver on the
 * same `document.documentElement` subtree, so a big batch of inserted
 * nodes (a soft-nav swap, a quick-add render) gets walked once per
 * consumer via one shared observer callback instead of once per consumer
 * per independent observer instance.
 * @type {Array<(root: Element) => void>}
 */
const elementsAddedCallbacks = [];

/** @param {(root: Element) => void} callback */
export function onElementsAdded(callback) {
  elementsAddedCallbacks.push(callback);
}

// Soft-nav section swaps, quick-add renders, drawer content, etc. all insert
// markup after this initial scan — catch those too.
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      scan(node);
      for (const callback of elementsAddedCallbacks) callback(node);
    }
    for (const node of mutation.removedNodes) {
      if (!(node instanceof Element)) continue;
      unobserveRemoved(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

// The event list component.js's registerEventListeners() delegates through,
// minus the expensive pointerenter/pointerleave pair (not useful as an early
// signal here — by the time they'd fire, pointerdown already has). Imported
// rather than hand-copied so the two lists can't silently drift apart.
const INTERACTION_SELECTOR = INTERACTION_EVENTS.map((name) => `[on\\:${name}]`).join(',');

/**
 * Every configured tag, regardless of its own trigger — used to find
 * nested components below, not to decide how any one of them normally
 * loads.
 */
const ALL_TAGS_SELECTOR = Object.keys(MODULES).join(',');

/**
 * A deliberately simplified approximation of component.js's own
 * getClosestComponent() resolution (assets/component.js) for the empty-
 * selector case — that real function only matches an ALREADY-UPGRADED
 * Component instance or a tag name ending in "-component", which can't
 * work here since the whole point of this loader is to identify a module
 * BEFORE it's loaded/upgraded. None of the 'interaction' tags configured
 * above rely on the empty-selector fallback (filters-drawer uses an #id
 * selector), so this only needs to handle the selector-provided cases
 * correctly today — the `if (!selector) return trigger` fallback exists
 * for shape-completeness with component.js's own attribute syntax, not
 * because any current entry exercises it.
 * @param {Element} trigger
 * @param {string} attributeValue
 */
function resolveInteractionTarget(trigger, attributeValue) {
  const [selector] = attributeValue.split('/');
  if (!selector) return trigger;
  return selector.startsWith('#') ? document.querySelector(selector) : trigger.closest(selector);
}

/** @param {Event} event */
function handleInteraction(event) {
  const trigger = event.target instanceof Element ? event.target.closest(INTERACTION_SELECTOR) : null;
  if (!trigger) return;

  for (const eventName of INTERACTION_EVENTS) {
    const value = trigger.getAttribute(`on:${eventName}`);
    if (value === null) continue;
    const target = resolveInteractionTarget(trigger, value);
    if (!(target instanceof Element)) continue;

    const config = MODULES[target.localName];
    // Deliberately loadModule(), not schedule() — priority is not honoured
    // here, see the ModuleConfig doc comment above MODULES.
    if (config) loadModule(config.specifier);

    // `target` (e.g. <filters-drawer>) is about to become visible/usable —
    // anything 'lazy' nested inside it has been sitting off-canvas since
    // page load, meaning its own IntersectionObserver entry has never had
    // a non-zero intersection ratio to fire on. It WOULD still eventually
    // fire once the container's own reveal makes it visible — but only
    // after that geometry change, the observer's callback, and then its
    // own import(), a real, visible lag layered on top of the container's
    // now-instant interaction-triggered load: the drawer visibly opens
    // showing stale, not-yet-upgraded content until it catches up. Loading
    // every nested component immediately, in parallel with the container
    // itself, closes that gap. Deliberately not scoped to nested tags
    // whose OWN trigger is 'interaction' — any 'lazy' descendant of a
    // revealed container has the identical problem.
    for (const nested of target.querySelectorAll(ALL_TAGS_SELECTOR)) {
      const nestedConfig = MODULES[nested.localName];
      if (nestedConfig) loadModule(nestedConfig.specifier);
    }
  }
}

if (interactionTags.length > 0) {
  document.addEventListener('pointerdown', handleInteraction, { capture: true });
  document.addEventListener('focusin', handleInteraction, { capture: true });
}
